import type { StowedElement } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { GeometricPersistOp } from '@/lib/elements/types/geometric-persist-op'
import {
  buildStowedElement,
  buildStowedVisibleRectOptions,
} from '@/lib/elements/stowed-element-builder'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

import { isGroupShape } from '@/lib/groups/group-kind'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'

/**
 * Builds the `StowedElement[]` body for PUT …/elements/move|resize|rotate (or batch update):
 * the kind-7 group row first, then every **non–kind-7** shape in its subtree (canvas-absolute
 * geometry), in stable list order.
 *
 * Pass `geometricOperation` so each row gets `visible*` when required (rotate, or move/resize while rotated).
 */
export function buildGroupGeometricBatchBodies(
  shapesInListOrder: ShapeData[],
  group: ShapeData,
  geometricOperation?: GeometricPersistOp,
  projection: DocumentProjection = IDENTITY_PROJECTION,
  fonts?: ParagraphBlockFontLookup
): StowedElement[] {
  if (!isGroupShape(group)) {
    return [
      buildStowedElement(
        group,
        buildStowedVisibleRectOptions(group.rotation, geometricOperation),
        projection,
        fonts
      ),
    ]
  }

  const byId = new Map(shapesInListOrder.map((s) => [s.id, s]))
  const nonGroupDescendants = shapesInListOrder.filter(
    (s) => s.id !== group.id && isDescendantOfGroup(s, group.id, byId) && !isGroupShape(s)
  )

  return [
    buildStowedElement(
      group,
      buildStowedVisibleRectOptions(group.rotation, geometricOperation),
      projection,
      fonts
    ),
    ...nonGroupDescendants.map((s) =>
      buildStowedElement(
        s,
        buildStowedVisibleRectOptions(s.rotation, geometricOperation),
        projection,
        fonts
      )
    ),
  ]
}
